import { useEffect, useRef } from "react";
import { HubExchangeAccount, WidgetProps, SocialName, TradeToEarnCampaign } from "@cede/types";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { useSocialAccounts } from "@/hooks/socials/useSocialAccounts";
import { useHubUser } from "@/hooks/user/useHubUser";
import { FLOW_TYPES, STEPS, useConnectStore } from "@/store/connectStore";

// Minimal interface for connect modal that works with both campaign types
interface ConnectModalMetadata {
  id: string | number;
  name: string;
  exchangeIds: string[];
}

type UseConnectAccountsModalProps = {
  campaignMetadata: TradeToEarnCampaign | ConnectModalMetadata | null;
  socialAccountsRequired: SocialName[];
  isAccessLoading: boolean;
  access: boolean | undefined;
  tradeWidgetConfig: WidgetProps | null;
  /** Set to false to disable the modal (e.g., for onchain campaigns that don't need CEX connections) */
  enabled?: boolean;
};

export function useConnectAccountsModal({
  campaignMetadata,
  socialAccountsRequired,
  isAccessLoading,
  access,
  tradeWidgetConfig,
  enabled = true,
}: UseConnectAccountsModalProps) {
  const { hubUser } = useHubUser();
  const {
    openConnectModal,
    setOpenConnectModal,
    setExchangeIds,
    setFlowType,
    flowType,
    setActiveStep,
    setSocialAccountsRequired,
  } = useConnectStore();
  const modalWasAlreadyOpenAutomatically = useRef(false);
  const { hasRequiredSocialAccounts } = useSocialAccounts();
  const isRelink = flowType === FLOW_TYPES.RELINK;

  // Handle automatic modal opening for exchange connections
  useEffect(() => {
    if (
      !enabled ||
      modalWasAlreadyOpenAutomatically.current ||
      !hubUser ||
      !campaignMetadata ||
      isAccessLoading ||
      openConnectModal
    )
      return;
    const notConnectedExchanges =
      tradeWidgetConfig?.config?.exchangeIds?.filter(
        (id) => !hubUser?.exchangeAccounts.some((account: HubExchangeAccount) => account.exchangeId === id),
      ) || [];

    // Check if user has connected exchanges but no social accounts
    const hasConnectedExchanges = hubUser?.exchangeAccounts.length > 0;

    if (notConnectedExchanges.length > 0 && access && !isRelink) {
      // User has missing exchange connections - start normal flow
      setSocialAccountsRequired(socialAccountsRequired);
      setExchangeIds(campaignMetadata.exchangeIds || Object.values(SUPPORTED_EXCHANGES));
      setFlowType(FLOW_TYPES.READY_TO_USE);
      setOpenConnectModal(true);
      modalWasAlreadyOpenAutomatically.current = true;
    } else if (hasConnectedExchanges && !hasRequiredSocialAccounts(socialAccountsRequired) && access && !isRelink) {
      // User has exchanges connected but no social accounts - go to LinkSocials step
      setSocialAccountsRequired(socialAccountsRequired);
      setExchangeIds(campaignMetadata.exchangeIds || Object.values(SUPPORTED_EXCHANGES));
      setFlowType(FLOW_TYPES.READY_TO_USE);
      setActiveStep(STEPS.LinkSocials);
      setOpenConnectModal(true);
      modalWasAlreadyOpenAutomatically.current = true;
    }
  }, [
    enabled,
    hubUser,
    tradeWidgetConfig,
    campaignMetadata,
    access,
    isAccessLoading,
    isRelink,
    openConnectModal,
    setActiveStep,
    setExchangeIds,
    setFlowType,
    setOpenConnectModal,
    setSocialAccountsRequired,
    socialAccountsRequired,
  ]);
}
